import { useEffect, useRef } from "react";
import { useSocket } from "../socket/SocketContext";
import { useStatus } from "./NotificationContext";

function humanizeCommandType(commandType = "") {
  const raw = String(commandType || "").trim();
  if (!raw) return "Command";
  return raw
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function buildToastMessage(payload = {}, fallback = "") {
  const response = String(payload.response || "").trim();
  if (response) return response;
  return fallback;
}

export default function CommandResponsePopup() {
  const { on } = useSocket();
  const { showStatus } = useStatus();
  const seenRef = useRef(new Set());

  useEffect(() => {
    const remember = (key) => {
      if (!key) return false;
      if (seenRef.current.has(key)) return true;
      seenRef.current.add(key);
      if (seenRef.current.size > 200) {
        const items = Array.from(seenRef.current);
        seenRef.current = new Set(items.slice(items.length - 120));
      }
      return false;
    };

    const handleSuccess = (payload = {}) => {
      const dedupeKey = `${payload.commandId || payload.imei || "cmd"}:success:${payload.status || ""}`;
      if (remember(dedupeKey)) return;

      const title = `${humanizeCommandType(payload.commandType)} Success`;
      const message = buildToastMessage(payload, "Device executed the command successfully.");

      showStatus("success", message, { title, duration: 7000, preserveMessage: true });
      window.dispatchEvent(new CustomEvent("qylmox:command-status", { detail: payload }));
    };

    const handleFailed = (payload = {}) => {
      const dedupeKey = `${payload.commandId || payload.imei || "cmd"}:failed:${payload.status || ""}`;
      if (remember(dedupeKey)) return;

      const title = `${humanizeCommandType(payload.commandType)} Failed`;
      const message = buildToastMessage(payload, "Device rejected the command.");

      showStatus("error", message, { title, duration: 8000, preserveMessage: true });
      window.dispatchEvent(new CustomEvent("qylmox:command-status", { detail: payload }));
    };

    const handleQueued = (payload = {}) => {
      const dedupeKey = `${payload.commandId || payload.imei || "cmd"}:queued`;
      if (remember(dedupeKey)) return;

      const title = `${humanizeCommandType(payload.commandType)} Queued`;
      const message = buildToastMessage(
        payload,
        "Device is offline right now. Command is queued and will be sent when it reconnects."
      );

      showStatus("info", message, { title, duration: 6500, preserveMessage: true });
      window.dispatchEvent(new CustomEvent("qylmox:command-status", { detail: payload }));
    };

    const offSuccess = on("command:success", handleSuccess);
    const offFailed = on("command:failed", handleFailed);
    const offQueued = on("command:queued", handleQueued);

    return () => {
      offSuccess?.();
      offFailed?.();
      offQueued?.();
    };
  }, [on, showStatus]);

  return null;
}
